import { useState, useCallback, useEffect } from 'react'
import { useApiFetch } from '../../hooks/useApiFetch.js'
import { useAdminStore } from '../../stores/adminStore.js'
import { StatCard, Select, Button, Table, Pagination, Alert, type TableColumn } from '../../components/ui/index.js'
import { StatusBadge } from '../../components/admin/index.js'
import { formatDate, formatNumber, formatCredits } from '../../lib/format.js'
import styles from './AdminCreditTransactions.module.css'

interface CreditTransaction {
  id: string
  type: string
  amount: number
  balanceAfter: number
  referenceType: string | null
  referenceId: string | null
  description: string | null
  createdAt: string
  userName: string | null
  userEmail: string
}

interface CreditTxResponse {
  transactions: CreditTransaction[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
  stats: { totalCount: number; totalDeducted: number; totalRefunded: number }
}

const TYPE_LABELS: Record<string, string> = {
  DEDUCT: '차감',
  REFUND: '환불',
  PURCHASE: '구매',
  BONUS: '보너스',
}

export function AdminCreditTransactions() {
  const { filters, setFilters, resetFilters, selectedUserId, setSelectedUserId } = useAdminStore()
  const [typeFilter, setTypeFilter] = useState('')

  useEffect(() => {
    resetFilters()
  }, [resetFilters])

  const params = new URLSearchParams()
  params.set('page', String(filters.page))
  params.set('pageSize', String(filters.pageSize))
  params.set('sortBy', filters.sortBy)
  params.set('sortOrder', filters.sortOrder)
  if (typeFilter) params.set('type', typeFilter)
  if (selectedUserId) params.set('userId', selectedUserId)

  const { data, isLoading, error } = useApiFetch<CreditTxResponse>(
    `/api/admin/credit-transactions?${params.toString()}`
  )

  const handleSort = useCallback((key: string, order: 'asc' | 'desc') => {
    setFilters({ sortBy: key, sortOrder: order })
  }, [setFilters])

  const columns: TableColumn<CreditTransaction>[] = [
    { key: 'id', label: 'ID', render: (row) => row.id.slice(0, 8), width: '80px' },
    { key: 'userName', label: '유저', render: (row) => row.userName ?? row.userEmail },
    { key: 'type', label: '타입', render: (row) => <StatusBadge status={row.type} label={TYPE_LABELS[row.type] ?? row.type} />, width: '90px' },
    {
      key: 'amount',
      label: '수량',
      sortable: true,
      render: (row) => {
        const isNegative = row.type === 'DEDUCT'
        return <span className={isNegative ? styles.negative : styles.positive}>{isNegative ? '-' : '+'}{formatNumber(row.amount)}</span>
      },
      align: 'right' as const,
      width: '80px',
    },
    { key: 'balanceAfter', label: '잔액', sortable: true, render: (row) => formatCredits(row.balanceAfter), align: 'right' as const, width: '80px' },
    { key: 'referenceType', label: '참조', render: (row) => row.referenceType ?? '-', width: '80px' },
    { key: 'description', label: '설명', render: (row) => row.description ?? '-' },
    { key: 'createdAt', label: '일시', sortable: true, render: (row) => formatDate(row.createdAt, 'yyyy.M.d HH:mm'), width: '130px' },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.statGrid}>
        <StatCard label="총 트랜잭션" value={`${formatNumber(data?.stats.totalCount ?? 0)}건`} />
        <StatCard label="총 차감" value={formatCredits(data?.stats.totalDeducted ?? 0)} />
        <StatCard label="총 환불" value={formatCredits(data?.stats.totalRefunded ?? 0)} />
      </div>

      <div className={styles.filters}>
        <Select
          size="sm"
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setFilters({ page: 1 }) }}
          options={[
            { value: '', label: '전체 타입' },
            { value: 'DEDUCT', label: '차감' },
            { value: 'REFUND', label: '환불' },
            { value: 'PURCHASE', label: '구매' },
            { value: 'BONUS', label: '보너스' },
          ]}
        />
        {selectedUserId && (
          <Button variant="ghost" size="sm" onClick={() => setSelectedUserId(null)}>
            유저 필터 해제
          </Button>
        )}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table<CreditTransaction>
        columns={columns}
        data={data?.transactions ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={handleSort}
      />

      {data?.pagination && data.pagination.totalPages > 1 && (
        <Pagination
          current={data.pagination.page}
          total={data.pagination.totalPages}
          onChange={(page) => setFilters({ page })}
          size="sm"
        />
      )}
    </div>
  )
}
